import React, { useState, useMemo, useEffect } from 'react';
import { useTable, useSortBy } from 'react-table';
import './SchoolsTable.css';

function SchoolsTable({ schools, onSchoolClick, onRefreshSchools }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [testFilter, setTestFilter] = useState('all'); // all | hide | only (internal test accounts)
  // Optimistic local overrides so the toggle flips instantly (no wait on the PATCH + refetch).
  const [internalOverrides, setInternalOverrides] = useState({});
  const isInternal = (s) => (s && s.id in internalOverrides ? internalOverrides[s.id] : !!(s && s.internal));

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa(`${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`)
  });

  // Flag/unflag a school as an internal test account, straight from the list. Updates the UI
  // immediately; the server call runs in the background and only reverts on failure.
  const toggleInternal = async (school, e) => {
    e.stopPropagation(); // don't open the school detail
    const next = !isInternal(school);
    setInternalOverrides(prev => ({ ...prev, [school.id]: next }));
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/schools/${school.id}/internal`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ internal: next })
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
    } catch (err) {
      console.error('Error toggling internal:', err);
      setInternalOverrides(prev => ({ ...prev, [school.id]: !next })); // revert
      alert('Failed to update the internal flag. Please try again.');
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => <span>{row.original.name}</span>,
      },
      {
        Header: 'District',
        accessor: 'district',
      },
      {
        Header: 'Timezone',
        accessor: 'timezone',
      },
      {
        Header: 'Account',
        accessor: 'internal',
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            className={`test-toggle ${isInternal(row.original) ? 'on' : ''}`}
            onClick={(e) => toggleInternal(row.original, e)}
            title={isInternal(row.original) ? 'Internal account. Click to mark as customer' : 'Customer account. Click to mark as internal'}
          >
            {isInternal(row.original) ? 'Internal' : 'Customer'}
          </button>
        ),
      },
    ],
    [internalOverrides]
  );

  // Filter schools based on search query + internal-test filter
  const filteredSchools = useMemo(() => {
    let rows = schools;
    if (testFilter === 'hide') rows = rows.filter(s => !isInternal(s));
    else if (testFilter === 'only') rows = rows.filter(s => isInternal(s));
    if (!debouncedSearchQuery) return rows;

    const query = debouncedSearchQuery.toLowerCase();
    return rows.filter(school => {
      const id = school.id ? school.id.toString().toLowerCase() : '';
      const name = school.name ? school.name.toLowerCase() : '';
      const district = school.district ? school.district.toLowerCase() : '';

      return id.includes(query) || name.includes(query) || district.includes(query);
    });
  }, [schools, debouncedSearchQuery, testFilter, internalOverrides]);

  // Use the useTable hook with sorting
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: filteredSchools,
      initialState: {
        sortBy: [
          {
            id: 'name',
            desc: false
          }
        ]
      }
    },
    useSortBy
  );

  return (
    <>
      <div className="content-header">
        <h2>Schools ({schools.length})</h2>
        <div className="list-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, district, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <select
            className="test-filter-select"
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value)}
            title="Internal test accounts"
          >
            <option value="all">All accounts</option>
            <option value="hide">Hide internal</option>
            <option value="only">Only internal</option>
          </select>
        </div>
      </div>
      
      {schools.length === 0 ? (
        <div className="no-data">No schools found</div>
      ) : filteredSchools.length === 0 ? (
        <div className="no-data">No schools match your search</div>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="schools-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={j}>
                      <div className="th-content">
                        {column.render('Header')}
                        <span className="sort-indicator">
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' ↓'
                              : ' ↑'
                            : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr 
                    {...row.getRowProps()} 
                    key={i}
                    onClick={() => onSchoolClick(row.original)}
                    className="clickable-row"
                  >
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default SchoolsTable;

